import { TreeMixin, itemTypeLoadMore, itemTypeDefault } from './mixin';
import ApiService from '@/services/apiService';
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import clone from 'lodash/clone';
import get from 'lodash/get';
const itemTypeStatesRoot = 'DtwinStatesRoot.';
const itemTypeState = 'DtwinState.';
export default {
  name: 'CoreDtwinTreeStates',
  mixins: [TreeMixin],
  props: {
    searchEnabled: {
      type: Boolean,
      required: false,
      default: () => true
    },
    selectedStates: {
      type: Array,
      required: false,
      default: () => []
    }
  },
  data() {
    return {
      statesForDtwins: {}
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'permissions'])
  },
  watch: {
    selectedStates() {
      this.cleanTreeSelection();
    }
  },
  created() {
    this.loadDescendents = false;
    this.itemTypeLoadMore = itemTypeLoadMore;
    this.itemTypeState = itemTypeState;
    this.itemTypeStatesRoot = itemTypeStatesRoot;
  },
  mounted() {
    this.initTree();
  },
  methods: {
    initTree() {
      this.twinTreeItems = [];
      this.twinItemMapping = {};
      this.twinItemMapping[this.parentId] = {};

      // an active node is specified, so make sure to load the tree-structure to accomodate this node
      // might not be on root-level, so there is special handling required to have it displayed right away
      if (this.activeDtwinId) {
        this.fetchPartialTree(this.activeDtwinId);
      } else {
        this.fetchChildren({
          id: this.parentId
        }, true);
      }
    },
    fetchChildren(item, initially = false) {
      const statesPromise = this.fetchChildrenStates(item);
      if (statesPromise) {
        return statesPromise;
      }
      return this.fetchChildrenDefault(item.id, 0, initially);
    },
    cleanTreeSelection() {
      const iterateChildren = children => {
        children.forEach(child => {
          if (typeof child.id === 'string' && this.itemType(child) === itemTypeState) {
            const stateId = parseInt(child.id.replace(itemTypeState, ''));
            if (this.selectedStates.findIndex(s => s.id === stateId) < 0) {
              child.selected = false;
            }
          } else {
            if (child.children) {
              iterateChildren(child.children);
            }
          }
        });
      };
      iterateChildren(this.twinTreeItems);
      // force a refresh
      this.twinTreeItems = clone(this.twinTreeItems);
    },
    processTreeItems(items) {
      // regular twins need to have a "states"-child added, that in turn contains all states with checkboxes
      // so handle every item that is NOT a state-item by manipulating their children
      this.processTreeItemsStates(items.filter(item => !this.treeItemIsState(item)));

      // next handle all state-items, i.e. put pagination there if needed and stuff
      this.processTreeItemsDefault(items.filter(item => this.treeItemIsState(item)));
    },
    itemType(item) {
      const itemId = get(item, 'id');
      if (typeof itemId === 'string') {
        if (itemId.startsWith(itemTypeLoadMore)) {
          return itemTypeLoadMore;
        }
        if (itemId.startsWith(itemTypeStatesRoot)) {
          return itemTypeStatesRoot;
        }
        if (itemId.startsWith(itemTypeState)) {
          return itemTypeState;
        }
      }
      return itemTypeDefault;
    },
    getTwinTreeIcon(dtwin) {
      // special case for the "Load More" tree item
      if (this.itemType(dtwin) === itemTypeLoadMore) {
        return this.getTwinTreeIconDefault(dtwin);
      }
      return this.getTwinTreeIconStates(dtwin);
    },
    // --------
    // STATES
    // --------

    fetchChildrenStates(item) {
      if (this.itemType(item) === itemTypeStatesRoot) {
        return this.fetchBindingsForDtwinStateItems([item]).then(() => {
          this.refreshTree();
        });
      }
      return null;
    },
    fetchBindingsForDtwinStateItems(items) {
      const itemsToBeFetched = items.filter(item => {
        const dtwinId = item.parentId;
        let dtwinStates = this.statesForDtwins[dtwinId];
        if (dtwinStates) {
          if (dtwinStates.length === 0) {
            this.removeStatesElement(this.twinTreeItems, dtwinId);
          } else {
            let children = item.children;
            if (children) {
              dtwinStates.forEach(state => {
                if (!children.find(child => child.id === state.id)) {
                  children.push(state);
                }
              });
            }
          }
          return false;
        }
        return true;
      });
      const dtwinIds = itemsToBeFetched.map(item => item.parentId);
      if (dtwinIds.length > 0) {
        return ApiService.digitalTwinsFetchStates(this, dtwinIds, ['bare']).then(states => {
          states.forEach(state => {
            if (!this.statesForDtwins[state.dtwinId]) {
              this.statesForDtwins[state.dtwinId] = [];
            }
            this.statesForDtwins[state.dtwinId].push(state);
          });
          dtwinIds.forEach(dtwinId => {
            let dtwinStates = get(this.statesForDtwins, dtwinId, []);
            dtwinStates.forEach(state => {
              state.title = state.properties.title;
              state.selected = this.stateIsSelected(state);
              state.parentId = state.id;
              if (!`${state.id}`.startsWith(itemTypeState)) {
                state.id = itemTypeState + state.id;
              }
            });
            this.statesForDtwins[dtwinId] = dtwinStates.sort((state1, state2) => {
              return state1.key < state2.key ? -1 : 1;
            });
            dtwinStates = this.statesForDtwins[dtwinId];
            if (dtwinStates.length === 0) {
              this.removeStatesElement(this.twinTreeItems, dtwinId);
            } else {
              let children = items.find(item => item.parentId === dtwinId).children;
              if (children) {
                dtwinStates.forEach(state => {
                  if (!children.find(child => child.id === state.id)) {
                    children.push(state);
                  }
                });
              }
            }
            this.statesForDtwins[dtwinId] = dtwinStates;
          });
          this.twinTreeItems = clone(this.twinTreeItems);
        });
      }
      return Promise.resolve();
    },
    processTreeItemsStates(items) {
      let fetchStatesForChildren = [];
      items.forEach(item => {
        // when children are requested/processed, their respective states are loaded as well (down here),
        // BUT they are not displayed yet, along with the true children of the twin, because the tree-node has not been opened yet
        // for the vuetify-tree to perform async loading, the element needs to have an empty `children` array, this is where
        // it gets compliated: if it is filled already, vuetify does not perform any async fetching any more,
        // if it does not exist, the node gets displayed as leaf without children
        // so we can only add the states-node as child of an item that already has been fetched, once its children are fetched
        // THUS, wait until a child is processed and then add the states-root-node as first child

        // check if the parent-element of item needs to have the twinStateChild added
        const parentTwin = this.twinItemMapping[item.parentId];
        const parentStatesNode = this.twinItemMapping[itemTypeStatesRoot + item.parentId];
        if (parentTwin && parentStatesNode) {
          if (parentTwin.children && (parentTwin.children.length === 0 || typeof parentTwin.children[0].id !== 'string')) {
            parentTwin.children = [parentStatesNode].concat(parentTwin.children);
          }
        }
        if (item.id && typeof item.id !== 'string') {
          this.twinItemMapping[item.id] = item;
          const twinStateChild = {
            id: itemTypeStatesRoot + item.id,
            parentId: item.id,
            title: this.$i18n_t('DigitalTwin.DigitalTwin.dataStates'),
            children: []
          };
          fetchStatesForChildren.push(twinStateChild);
          this.twinItemMapping[twinStateChild.id] = twinStateChild;
          if (!item.children) {
            item.children = [];
          }

          // this item has no children, so add the "STATES" child, if not added already
          if (item.childCount === 0) {
            item.children = [twinStateChild];
            return null;
          }

          // otherwise this item SHOULD have children, so add the "STATES" child once there is at least 1 valid child
          // otherwise the async lazy loading will not trigger any more
          if (item.children.length && typeof item.children[0].id !== 'string') {
            item.children = [twinStateChild].concat(item.children);
          }
        }
      });
      this.fetchBindingsForDtwinStateItems(fetchStatesForChildren);
    },
    getTwinTreeIconStates(dtwin) {
      if (this.itemType(dtwin) === itemTypeStatesRoot) {
        return 'fal fa-network-wired';
      }
      return undefined;
    },
    treeItemIsState(item) {
      return this.itemType(item) === itemTypeState;
    },
    removeStatesElement(treeItems, dtwinId) {
      delete this.twinItemMapping[itemTypeStatesRoot + dtwinId];
      const foundDtwinNode = treeItems.find(item => item.id === parseInt(dtwinId));
      if (foundDtwinNode) {
        // delete the "STATES" child for this node, identifiable by a 'twin_states' prefix
        foundDtwinNode.children = foundDtwinNode.children.filter(child => this.itemType(child) !== itemTypeStatesRoot);
        return true;
      }
      treeItems.forEach(item => {
        if (item.children) {
          if (this.removeStatesElement(item.children, dtwinId)) {
            return;
          }
        }
      });
    },
    stateSelectionToggled(item) {
      let cleanItem = cloneDeep(item);
      cleanItem.id = cleanItem.parentId;
      if (typeof cleanItem.id === 'string') {
        cleanItem.id = parseInt(cleanItem.id.replace(itemTypeState, ''));
      }
      if (this.stateIsSelected(cleanItem)) {
        // remove from list
        this.$emit('deselectedState', cleanItem);
      } else {
        // add to list
        this.$emit('selectedState', cleanItem);
      }
    },
    stateIsSelected(item) {
      return this.selectedStates.find(selectedState => selectedState.dtwinId === item.dtwinId && selectedState.id === item.id) !== undefined;
    }
  }
};